/**
 * Mutable builder for the normalized Defense Technology Intelligence graph.
 *
 * Both API adapters (`publicationsAdapter`, `procurementAdapter`) write into
 * one shared builder, which is where cross-dataset entity fusion happens:
 * nodes are keyed by their normalized id (see `entityResolver.makeNodeId`),
 * so the same organization observed in Publications and PLA Contracts
 * upserts into a single node while accumulating aliases, datasets, evidence
 * and record counts from both sources.
 *
 * Edges are keyed by `source|type|target`; repeated observations of the same
 * relationship merge into one edge whose `provenance` array lists every
 * supporting API record, keeping the structure ready for future
 * shortest-path / weighted-path analysis (edge weight = provenance length).
 */
import {
  IntelCluster,
  IntelClusterKey,
  IntelDataset,
  IntelEdge,
  IntelEdgeType,
  IntelEvidence,
  IntelNode,
  IntelNodeType,
  IntelRelationshipType,
  IntelligenceGraph,
  IntelligenceGraphSnapshot,
} from '../../types/intelligenceGraph';
import { makeNodeId, mergeNodeTypes } from './entityResolver';

/** Cap stored evidence per node so high-degree nodes stay lightweight. */
const MAX_NODE_EVIDENCE = 50;
/** Cap merged provenance per edge. */
const MAX_EDGE_PROVENANCE = 50;

export interface UpsertNodeInput {
  type: IntelNodeType;
  /** Raw source label (preserved as an alias). */
  label: string;
  /** Pre-computed id; defaults to `makeNodeId(type, label)`. */
  id?: string;
  dataset: IntelDataset;
  evidence?: IntelEvidence;
  isRoot?: boolean;
  plaEvidence?: string;
  meta?: Record<string, string | undefined>;
}

export interface UpsertEdgeInput {
  source: string;
  target: string;
  type: IntelEdgeType;
  sourceDataset: IntelDataset;
  sourceRecordId: string;
  evidence?: string;
  url?: string;
  confidence?: number;
  relationshipType?: IntelRelationshipType;
}

export function edgeKey(
  source: string,
  type: IntelEdgeType,
  target: string,
): string {
  return `${source}|${type}|${target}`;
}

export class IntelligenceGraphBuilder {
  readonly graph: IntelligenceGraph;

  /**
   * Upstream external ids (e.g. procurement `tenderCompanyId`) → resolved
   * graph node id. Lets the same company observed under name variants fuse
   * onto one node whenever the source APIs provide stable company ids.
   */
  private readonly externalIds = new Map<string, string>();

  constructor(base?: IntelligenceGraphSnapshot) {
    this.graph = {
      nodes: new Map(base?.nodes.map((n) => [n.id, { ...n }]) ?? []),
      edges: new Map(
        base?.edges.map((e) => [
          edgeKey(e.source, e.type, e.target),
          { ...e },
        ]) ?? [],
      ),
    };
  }

  /** Registers an upstream external id as resolving to a graph node id. */
  registerExternalId(externalId: string, nodeId: string): void {
    if (!this.externalIds.has(externalId)) {
      this.externalIds.set(externalId, nodeId);
    }
  }

  /** Resolves a previously-registered upstream external id to a node id. */
  resolveExternalId(externalId: string): string | undefined {
    return this.externalIds.get(externalId);
  }

  upsertNode(input: UpsertNodeInput): IntelNode {
    const id = input.id ?? makeNodeId(input.type, input.label);
    const existing = this.graph.nodes.get(id);

    if (!existing) {
      const node: IntelNode = {
        id,
        type: input.type,
        label: input.label.trim(),
        aliases: [input.label.trim()],
        datasets: [input.dataset],
        recordCount: input.evidence ? 1 : 0,
        evidence: input.evidence ? [input.evidence] : [],
        isRoot: input.isRoot,
        plaEvidence: input.plaEvidence ? [input.plaEvidence] : undefined,
        meta: input.meta,
      };
      this.graph.nodes.set(id, node);
      return node;
    }

    existing.type = mergeNodeTypes(existing.type, input.type);
    const alias = input.label.trim();
    if (alias && !existing.aliases.includes(alias)) {
      existing.aliases.push(alias);
    }
    if (!existing.datasets.includes(input.dataset)) {
      existing.datasets.push(input.dataset);
    }
    if (input.evidence) {
      existing.recordCount += 1;
      const isDuplicate = existing.evidence.some(
        (e) =>
          e.sourceDataset === input.evidence?.sourceDataset &&
          e.sourceRecordId === input.evidence?.sourceRecordId,
      );
      if (!isDuplicate && existing.evidence.length < MAX_NODE_EVIDENCE) {
        existing.evidence.push(input.evidence);
      }
    }
    if (input.isRoot) existing.isRoot = true;
    if (input.plaEvidence) {
      existing.plaEvidence = existing.plaEvidence ?? [];
      if (!existing.plaEvidence.includes(input.plaEvidence)) {
        existing.plaEvidence.push(input.plaEvidence);
      }
      // Risk records classify the org as PLA rather than becoming graph nodes.
      existing.type = mergeNodeTypes(existing.type, 'pla');
    }
    if (input.meta) {
      existing.meta = { ...input.meta, ...existing.meta };
    }
    return existing;
  }

  upsertEdge(input: UpsertEdgeInput): IntelEdge | undefined {
    if (input.source === input.target) return undefined;
    const key = edgeKey(input.source, input.type, input.target);
    const existing = this.graph.edges.get(key);
    const provenance = {
      sourceDataset: input.sourceDataset,
      sourceRecordId: input.sourceRecordId,
      evidence: input.evidence,
      url: input.url,
    };

    if (!existing) {
      const edge: IntelEdge = {
        source: input.source,
        target: input.target,
        type: input.type,
        sourceDataset: input.sourceDataset,
        sourceRecordId: input.sourceRecordId,
        evidence: input.evidence,
        confidence: input.confidence ?? 1,
        relationshipType: input.relationshipType ?? 'observed',
        provenance: [provenance],
      };
      this.graph.edges.set(key, edge);
      return edge;
    }

    const isDuplicate = existing.provenance.some(
      (p) =>
        p.sourceDataset === input.sourceDataset &&
        p.sourceRecordId === input.sourceRecordId,
    );
    if (!isDuplicate && existing.provenance.length < MAX_EDGE_PROVENANCE) {
      existing.provenance.push(provenance);
    }
    // An observed report of a relationship upgrades a derived one.
    if (
      input.relationshipType !== 'derived' &&
      existing.relationshipType === 'derived'
    ) {
      existing.relationshipType = 'observed';
      existing.confidence = input.confidence ?? 1;
    } else {
      existing.confidence = Math.max(
        existing.confidence,
        input.confidence ?? 1,
      );
    }
    return existing;
  }

  snapshot(): IntelligenceGraphSnapshot {
    return {
      nodes: Array.from(this.graph.nodes.values()),
      edges: Array.from(this.graph.edges.values()),
    };
  }
}

const CLUSTER_DEFS: {
  key: IntelClusterKey;
  label: string;
  nodeType: IntelNodeType;
  match: (node: IntelNode) => boolean;
}[] = [
  {
    key: 'people',
    label: 'People',
    nodeType: 'person',
    match: (n) => n.type === 'person',
  },
  {
    key: 'research',
    label: 'Research',
    nodeType: 'research',
    match: (n) => n.type === 'research',
  },
  {
    key: 'technology',
    label: 'Technology',
    nodeType: 'technology',
    match: (n) => n.type === 'technology',
  },
  {
    key: 'pla',
    label: 'PLA Connections',
    nodeType: 'pla',
    match: (n) => n.type === 'pla',
  },
  {
    key: 'procurement',
    label: 'Procurement',
    nodeType: 'procurement',
    match: (n) => n.type === 'procurement',
  },
  {
    key: 'buyers',
    label: 'Buyers',
    nodeType: 'buyer',
    match: (n) => n.type === 'buyer',
  },
  {
    key: 'suppliers',
    label: 'Suppliers',
    nodeType: 'supplier',
    match: (n) => n.type === 'supplier',
  },
  {
    key: 'agencies',
    label: 'Procurement Agencies',
    nodeType: 'agency',
    match: (n) => n.type === 'agency',
  },
];

/**
 * Groups the non-root neighborhood of `rootId` into category clusters
 * (People / Research / Technology / PLA / Procurement / Buyers / Suppliers /
 * Agencies), each ranked by record count and connection count so cluster
 * expansion can reveal the highest-value nodes first.
 */
export function buildClusters(
  snapshot: IntelligenceGraphSnapshot,
  rootId: string,
): IntelCluster[] {
  const degree = new Map<string, number>();
  snapshot.edges.forEach((e) => {
    degree.set(e.source, (degree.get(e.source) ?? 0) + 1);
    degree.set(e.target, (degree.get(e.target) ?? 0) + 1);
  });

  const score = (n: IntelNode) => n.recordCount * 2 + (degree.get(n.id) ?? 0);

  return CLUSTER_DEFS.map((def) => {
    const members = snapshot.nodes
      .filter((n) => n.id !== rootId && def.match(n))
      .sort((a, b) => score(b) - score(a));
    return {
      key: def.key,
      label: def.label,
      nodeType: def.nodeType,
      memberIds: members.map((n) => n.id),
      count: members.length,
    };
  }).filter((c) => c.count > 0);
}
